/**
 * Saved reports — persistence for build-your-own report cards
 * (`savedReports`).
 *
 * The Reports tab's builder lets an operator pick a metric + a display
 * (stat card / bar breakdown), name it, and save it. This module is the
 * store wrapper for that card — mirrors tickets.ts / savedViews.ts: a
 * thin, typed layer over the generic `db` so the view deals only in
 * `SavedReport` records, and the localStorage↔Firestore cutover stays a
 * one-line adapter swap in store.ts.
 *
 * WHY the id is `rpt:${slug(name)}`: a report is naturally unique by
 * name — re-saving "Pipeline this quarter" should REPLACE that card,
 * not pile up duplicates. Deterministic ids make every save an upsert
 * (same reasoning as savedViews' `${entity}:${slug}` keys).
 *
 * Honest accounting: nothing is seeded — an empty list means the
 * operator has saved no reports. The metric itself is computed live by
 * `computeReportMetric` at render time; a saved report stores WHAT to
 * compute, never a stale number.
 */

import { db, nowIso } from './store';
import type { SavedReport } from './types';
import type { ReportDisplay, ReportMetricKind } from '../reports/reportMetrics';

/** Everything the caller provides; id + timestamps are derived here. */
export interface SavedReportInput {
  name: string;
  metric: ReportMetricKind;
  display: ReportDisplay;
  createdBy?: string;
}

/** Deterministic doc id per name — makes saves upserts. */
export function savedReportId(name: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return `rpt:${slug}`;
}

/** Stable name ordering for lists + subscriptions (case-insensitive). */
function byName(a: SavedReport, b: SavedReport): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

/**
 * Save a report. Upserts by name: re-saving an existing name replaces
 * its metric/display but keeps the original id + createdAt, so the card
 * identity stays stable. Returns the persisted record.
 */
export async function saveReport(input: SavedReportInput): Promise<SavedReport> {
  const name = input.name.trim();
  if (!name) throw new Error('A saved report needs a name.');
  const id = savedReportId(name);
  const existing = await db.get('savedReports', id);
  const ts = nowIso();
  const record: SavedReport = {
    id,
    name,
    metric: input.metric,
    display: input.display,
    createdAt: existing?.createdAt ?? ts,
    updatedAt: ts,
    ...(input.createdBy !== undefined
      ? { createdBy: input.createdBy }
      : existing?.createdBy !== undefined
        ? { createdBy: existing.createdBy }
        : {}),
  };
  await db.put('savedReports', record);
  return record;
}

/** All saved reports, sorted by name. */
export async function listSavedReports(): Promise<SavedReport[]> {
  const all = await db.list('savedReports');
  return [...all].sort(byName);
}

/** One saved report by id, or null. */
export async function getSavedReport(id: string): Promise<SavedReport | null> {
  return db.get('savedReports', id);
}

/** Delete one saved report by id. */
export async function removeSavedReport(id: string): Promise<void> {
  await db.remove('savedReports', id);
}

/** Subscribe to saved reports, sorted by name. */
export function subscribeSavedReports(
  fn: (reports: SavedReport[]) => void,
): () => void {
  return db.subscribe('savedReports', (rows) => fn([...rows].sort(byName)));
}
